import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSettingsCtx } from "@/context/SettingsContext";

const fieldCls =
  "w-full min-h-[42px] rounded-md border border-[#D9E0E4] bg-white px-3 text-[14.5px] text-navy " +
  "focus:outline-none focus:ring-2 focus:ring-teal focus:border-teal transition-colors";

const labelCls = "block text-[12.5px] font-semibold text-navy mb-1.5";
const sectionCls = "rounded-lg border border-[#D9E0E4] bg-white p-5 space-y-4";
const sectionTitleCls = "font-heading font-bold text-[16px] text-navy";

function Field({ label, value, onChange, type = "text", placeholder, testId, textarea }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea
          className={`${fieldCls} py-2.5 min-h-[80px]`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
        />
      ) : (
        <input
          type={type}
          className={fieldCls}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
        />
      )}
    </div>
  );
}

function ListEditor({ title, items, onChange, columns, newItem, testIdPrefix }) {
  const add = () => onChange([...(items || []), { ...newItem }]);
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  const upd = (i, key, val) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));

  return (
    <div className={sectionCls}>
      <div className="flex items-center justify-between">
        <h3 className={sectionTitleCls}>{title}</h3>
        <button
          type="button"
          onClick={add}
          data-testid={`${testIdPrefix}-add`}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-teal hover:text-[#075652]"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="space-y-3">
        {(items || []).map((item, i) => (
          <div key={i} className="rounded-md border border-[#E3E9EC] bg-[#FBFCFC] p-3" data-testid={`${testIdPrefix}-row-${i}`}>
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(columns.length, 3)}, minmax(0,1fr))` }}>
              {columns.map((col) => (
                <div key={col.key} className={col.wide ? "col-span-full" : ""}>
                  <label className={labelCls}>{col.label}</label>
                  {col.textarea ? (
                    <textarea
                      className={`${fieldCls} py-2.5 min-h-[70px]`}
                      value={item[col.key] ?? ""}
                      onChange={(e) => upd(i, col.key, e.target.value)}
                    />
                  ) : (
                    <input
                      className={fieldCls}
                      type={col.type || "text"}
                      value={item[col.key] ?? ""}
                      onChange={(e) => upd(i, col.key, col.type === "number" ? Number(e.target.value) : e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => del(i)}
                className="inline-flex items-center gap-1 text-[12.5px] text-[#B42318] hover:underline"
                data-testid={`${testIdPrefix}-delete-${i}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        ))}
        {(!items || items.length === 0) && (
          <p className="text-[12.5px] text-muted-foreground italic">No items yet — click Add to create one.</p>
        )}
      </div>
    </div>
  );
}

export default function SettingsEditor() {
  const { settings, refresh } = useSettingsCtx();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [seoPath, setSeoPath] = useState("/");

  useEffect(() => { setDraft(settings); }, [settings]);

  const merge = (section, updater) => {
    setDraft((d) => ({ ...d, [section]: updater(d[section]) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company: draft.company,
        contact: draft.contact,
        socials: draft.socials,
        homepage: draft.homepage,
        seo: draft.seo,
        services: draft.services,
        markets: draft.markets,
        industries: draft.industries,
        faqs: draft.faqs,
      };
      await api.put("/settings", payload);
      await refresh();
      toast.success("Site settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const seoPaths = useMemo(() => {
    const paths = new Set(Object.keys(draft?.seo || {}));
    paths.add(seoPath);
    return Array.from(paths).sort();
  }, [draft?.seo, seoPath]);

  const currentSeo = (draft?.seo || {})[seoPath] || { title: "", description: "" };

  const updateSeo = (key, val) => {
    setDraft((d) => ({
      ...d,
      seo: { ...(d.seo || {}), [seoPath]: { ...currentSeo, [key]: val } },
    }));
  };

  const addSeoPath = () => {
    const p = window.prompt("Enter route path (e.g. /about)");
    if (!p) return;
    const path = p.startsWith("/") ? p : `/${p}`;
    setDraft((d) => ({ ...d, seo: { ...(d.seo || {}), [path]: { title: "", description: "" } } }));
    setSeoPath(path);
  };

  const c = draft?.company || {};
  const co = draft?.contact || {};
  const hp = draft?.homepage || {};

  return (
    <div className="space-y-6" data-testid="admin-settings-editor">
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b border-[#D9E0E4] flex items-center justify-between">
        <p className="text-[13px] text-muted-foreground">
          Edits go live on the public website after you press Save.
        </p>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-settings-save"
          className="inline-flex items-center gap-2 min-h-[42px] px-5 rounded-md bg-teal text-white font-semibold hover:bg-[#075652] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>

      {/* Company */}
      <div className={sectionCls}>
        <h3 className={sectionTitleCls}>Company</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Display name" value={c.name} onChange={(v) => merge("company", (o) => ({ ...o, name: v }))} testId="settings-company-name" />
          <Field label="Legal name" value={c.legalName} onChange={(v) => merge("company", (o) => ({ ...o, legalName: v }))} />
          <Field label="Tagline" value={c.tagline} onChange={(v) => merge("company", (o) => ({ ...o, tagline: v }))} />
          <Field label="Domain (no https://)" value={c.domain} onChange={(v) => merge("company", (o) => ({ ...o, domain: v }))} />
          <Field label="Company registration" value={c.registration} onChange={(v) => merge("company", (o) => ({ ...o, registration: v }))} />
        </div>
      </div>

      {/* Contact */}
      <div className={sectionCls}>
        <h3 className={sectionTitleCls}>Contact info</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Address (short)" value={co.address} onChange={(v) => merge("contact", (o) => ({ ...o, address: v }))} testId="settings-contact-address" />
          <Field
            label="Locations (comma separated)"
            value={(co.locations || []).join(", ")}
            onChange={(v) => merge("contact", (o) => ({ ...o, locations: v.split(",").map((s) => s.trim()).filter(Boolean) }))}
          />
          <Field label="Phone (display)" value={co.phone} onChange={(v) => merge("contact", (o) => ({ ...o, phone: v }))} testId="settings-contact-phone" />
          <Field label="Phone href (e.g. tel:+…)" value={co.phoneHref} onChange={(v) => merge("contact", (o) => ({ ...o, phoneHref: v }))} />
          <Field label="WhatsApp (display)" value={co.whatsapp} onChange={(v) => merge("contact", (o) => ({ ...o, whatsapp: v }))} />
          <Field
            label="WhatsApp number (digits only, no +)"
            value={co.whatsappNumber}
            onChange={(v) => merge("contact", (o) => ({ ...o, whatsappNumber: v.replace(/\D/g, "") }))}
          />
          <Field label="Email" value={co.email} onChange={(v) => merge("contact", (o) => ({ ...o, email: v }))} />
          <Field label="Opening hours" value={co.openingHours} onChange={(v) => merge("contact", (o) => ({ ...o, openingHours: v }))} />
          <Field label="Google Maps embed URL" value={co.mapsEmbed} onChange={(v) => merge("contact", (o) => ({ ...o, mapsEmbed: v }))} placeholder="https://www.google.com/maps/embed?…" />
          <Field label="Google Business profile URL" value={co.googleBusiness} onChange={(v) => merge("contact", (o) => ({ ...o, googleBusiness: v }))} placeholder="https://g.page/…" />
        </div>
      </div>

      {/* Socials */}
      <ListEditor
        title="Social links"
        items={draft?.socials}
        onChange={(items) => setDraft((d) => ({ ...d, socials: items }))}
        columns={[
          { key: "label", label: "Label" },
          { key: "href", label: "URL" },
          { key: "handle", label: "Handle (optional)" },
        ]}
        newItem={{ label: "", href: "", handle: "" }}
        testIdPrefix="settings-social"
      />

      {/* Homepage */}
      <div className={sectionCls}>
        <h3 className={sectionTitleCls}>Homepage hero</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={hp.heroEyebrow} onChange={(v) => merge("homepage", (o) => ({ ...o, heroEyebrow: v }))} testId="settings-hero-eyebrow" />
          <Field label="Primary CTA label" value={hp.heroPrimaryCta} onChange={(v) => merge("homepage", (o) => ({ ...o, heroPrimaryCta: v }))} />
          <Field label="Secondary CTA label" value={hp.heroSecondaryCta} onChange={(v) => merge("homepage", (o) => ({ ...o, heroSecondaryCta: v }))} />
        </div>
        <Field label="Hero title" value={hp.heroTitle} onChange={(v) => merge("homepage", (o) => ({ ...o, heroTitle: v }))} testId="settings-hero-title" textarea />
        <Field label="Hero subtitle" value={hp.heroSubtitle} onChange={(v) => merge("homepage", (o) => ({ ...o, heroSubtitle: v }))} textarea />
      </div>

      {/* SEO */}
      <div className={sectionCls}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className={sectionTitleCls}>SEO — per page</h3>
          <div className="flex items-center gap-2">
            <select
              value={seoPath}
              onChange={(e) => setSeoPath(e.target.value)}
              className={`${fieldCls} !min-h-[38px] max-w-[240px]`}
              data-testid="settings-seo-path-select"
            >
              {seoPaths.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <button onClick={addSeoPath} className="text-[13px] font-semibold text-teal hover:text-[#075652]" data-testid="settings-seo-add-path">
              + Add path
            </button>
          </div>
        </div>
        <Field label="Meta title" value={currentSeo.title} onChange={(v) => updateSeo("title", v)} testId="settings-seo-title" />
        <Field label="Meta description" value={currentSeo.description} onChange={(v) => updateSeo("description", v)} textarea />
      </div>

      {/* Services */}
      <ListEditor
        title="Services"
        items={draft?.services}
        onChange={(items) => setDraft((d) => ({ ...d, services: items }))}
        columns={[
          { key: "title", label: "Title" },
          { key: "slug", label: "Slug" },
          { key: "icon", label: "Icon name (lucide-react)" },
          { key: "path", label: "Route path" },
          { key: "short", label: "Short summary", textarea: true, wide: true },
          { key: "copy", label: "Long copy", textarea: true, wide: true },
        ]}
        newItem={{ title: "", slug: "", icon: "Search", path: "/sourcing-services", short: "", copy: "" }}
        testIdPrefix="settings-service"
      />

      {/* Markets */}
      <ListEditor
        title="Markets"
        items={draft?.markets}
        onChange={(items) => setDraft((d) => ({ ...d, markets: items }))}
        columns={[
          { key: "code", label: "Code (e.g. PK)" },
          { key: "name", label: "Name" },
          { key: "role", label: "Role" },
          { key: "note", label: "Note", textarea: true, wide: true },
          { key: "x", label: "Map X (0-100)", type: "number" },
          { key: "y", label: "Map Y (0-100)", type: "number" },
        ]}
        newItem={{ code: "", name: "", role: "", note: "", x: 50, y: 50 }}
        testIdPrefix="settings-market"
      />

      {/* Industries */}
      <ListEditor
        title="Industries"
        items={draft?.industries}
        onChange={(items) => setDraft((d) => ({ ...d, industries: items }))}
        columns={[
          { key: "title", label: "Title" },
          { key: "icon", label: "Icon name (lucide-react)" },
        ]}
        newItem={{ title: "", icon: "Boxes" }}
        testIdPrefix="settings-industry"
      />

      {/* FAQs */}
      <ListEditor
        title="FAQs"
        items={draft?.faqs}
        onChange={(items) => setDraft((d) => ({ ...d, faqs: items }))}
        columns={[
          { key: "q", label: "Question", wide: true },
          { key: "a", label: "Answer", textarea: true, wide: true },
        ]}
        newItem={{ q: "", a: "" }}
        testIdPrefix="settings-faq"
      />

      <div className="pt-2 pb-8 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-settings-save-bottom"
          className="inline-flex items-center gap-2 min-h-[46px] px-6 rounded-md bg-teal text-white font-semibold hover:bg-[#075652] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>
    </div>
  );
}
